import React from 'react';
import { useTranslation } from 'localization';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import type { ContextLevel } from 'types/context';
import './WalkDiagramAtom.scss';

export interface WalkDiagramAtomProps {
  /** Ordered levels of the context walk. */
  levels: ContextLevel[];
  /** Label for the root pill (typically the focus sphere name). */
  rootLabel?: string;
  /** Compact sizing for dense surfaces like the switcher popover. */
  compact?: boolean;
  /** Maximum level pills to show before collapsing into a "+N" indicator. */
  maxLevels?: number;
  className?: string;
}

/**
 * WalkDiagramAtom
 *
 * The recurring "shape of the walk" signature from the Context Selector design:
 * a dashed root pill followed by one pill per level, each prefixed by a
 * direction arrow (→ outwards / ← inwards) and an optional ×N repeat badge.
 *
 * Lightweight by design — communicates the walk's shape at a glance in library
 * cards, table rows, and the context switcher. For the detailed per-level
 * breakdown (filters, segments, settings) use ContextSummaryMolecule instead.
 */
const WalkDiagramAtom: React.FC<WalkDiagramAtomProps> = ({
  levels,
  rootLabel,
  compact = false,
  maxLevels = 3,
  className = '',
}) => {
  const { t } = useTranslation();
  const visible = levels.slice(0, maxLevels);
  const hiddenCount = levels.length - visible.length;
  const classes = [
    'walk-diagram',
    compact ? 'walk-diagram--compact' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} aria-label={t('walkDiagram.ariaLabel', { defaultValue: 'Context walk shape' })}>
      <span className="walk-diagram__root">
        <span className="walk-diagram__root-dot" aria-hidden="true" />
        {rootLabel || t('walkDiagram.rootFallback', { defaultValue: 'Root' })}
      </span>
      {visible.map((lv, i) => {
        const inwards = lv.direction === 'inwards';
        const label = getLocalizedText(lv.label, '');
        const repeat = lv.repeat ?? 1;
        return (
          <React.Fragment key={lv.id ?? i}>
            <span
              className={`walk-diagram__arrow${inwards ? ' walk-diagram__arrow--inwards' : ''}`}
              aria-hidden="true"
            >
              {inwards ? '←' : '→'}
            </span>
            <span className="walk-diagram__level">
              <span
                className={`walk-diagram__level-arrow${inwards ? ' walk-diagram__level-arrow--inwards' : ''}`}
                aria-hidden="true"
              >
                {inwards ? '←' : '→'}
              </span>
              <span className="walk-diagram__level-label">{label}</span>
              {repeat > 1 && <span className="walk-diagram__repeat">×{repeat}</span>}
            </span>
          </React.Fragment>
        );
      })}
      {hiddenCount > 0 && (
        <span className="walk-diagram__more">
          {t('walkDiagram.more', { count: hiddenCount, defaultValue: `+${hiddenCount} more` })}
        </span>
      )}
    </div>
  );
};

WalkDiagramAtom.displayName = 'WalkDiagramAtom';

export default WalkDiagramAtom;
